/**
 * ChartTooltip — the single glass-style tooltip + value chip used across
 * every chart in the Data Vault library.
 *
 * Style: backdrop-blur glass surface, subpixel-rendered typography,
 * monospace numerals, spring entrance (translate + opacity via the H1
 * easing palette). Floats in fixed positioning so it can escape parent
 * `overflow: hidden` containers.
 *
 * Two consumption patterns:
 *   1. `<ChartTooltip>` — declarative, controlled by `visible` + `x/y`.
 *      Use when the parent owns hover state.
 *   2. `<ValueChip>` — the inner content piece by itself (no positioning
 *      wrapper). Use when the hand-pointer (H6) carries the chip.
 *
 * Numerals: rendered in `font-variant-numeric: tabular-nums` so digits
 * don't jitter as values change. Monospace font for the value, sans for
 * labels.
 */

import { type ReactNode, useEffect, useState } from 'react';
import { EASINGS, DURATIONS } from './interactions/easings';

export type TooltipPlacement = 'top' | 'bottom' | 'left' | 'right';

interface ChartTooltipProps {
  /** Hide via opacity transition when false. */
  visible: boolean;
  /** Anchor X in viewport coordinates (use clientX). */
  x: number;
  /** Anchor Y in viewport coordinates (use clientY). */
  y: number;
  /** Where the tooltip sits relative to the anchor. Default 'top'. */
  placement?: TooltipPlacement;
  /** Offset from anchor in pixels (along the placement axis). Default 12. */
  offset?: number;
  children: ReactNode;
}

const PLACEMENT_TRANSFORM: Record<TooltipPlacement, (offset: number) => string> = {
  top:    (o) => `translate(-50%, calc(-100% - ${o}px))`,
  bottom: (o) => `translate(-50%, ${o}px)`,
  left:   (o) => `translate(calc(-100% - ${o}px), -50%)`,
  right:  (o) => `translate(${o}px, -50%)`,
};

/**
 * Glass tooltip floating in fixed positioning. Mount in any chart's
 * onPointerEnter / onPointerMove handler and update `x`, `y`, `visible`.
 */
export function ChartTooltip({
  visible,
  x,
  y,
  placement = 'top',
  offset = 12,
  children,
}: ChartTooltipProps) {
  // Delayed unmount so the exit transition completes before the DOM node
  // is removed. visibleNow starts true after a frame so the initial render
  // can transition from its hidden state.
  const [mounted, setMounted] = useState(visible);
  useEffect(() => {
    if (visible) {
      setMounted(true);
    } else {
      // 200ms matches DURATIONS.base.
      const id = setTimeout(() => setMounted(false), DURATIONS.base);
      return () => clearTimeout(id);
    }
  }, [visible]);

  if (!mounted) return null;

  return (
    <div
      style={{
        position: 'fixed',
        left: x,
        top: y,
        transform: PLACEMENT_TRANSFORM[placement](offset),
        zIndex: 60,
        pointerEvents: 'none',
        opacity: visible ? 1 : 0,
        // Tiny scale-in on appear; back-out spring for the "settle" feel.
        scale: visible ? '1' : '0.96',
        transformOrigin: 'center',
        transition: `opacity ${DURATIONS.fast}ms ${EASINGS.easeOut}, scale ${DURATIONS.fast}ms ${EASINGS.spring}`,
        // Glass surface.
        background: 'var(--dv-tooltip-bg, rgba(20, 24, 32, 0.78))',
        backdropFilter: 'blur(12px) saturate(140%)',
        WebkitBackdropFilter: 'blur(12px) saturate(140%)',
        border: '1px solid var(--dv-tooltip-border, rgba(255, 255, 255, 0.08))',
        borderRadius: 8,
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.25), 0 1px 2px rgba(0, 0, 0, 0.15)',
        padding: '6px 10px',
        // Type.
        color: 'var(--dv-tooltip-fg, rgba(255, 255, 255, 0.95))',
        fontSize: 11,
        lineHeight: 1.4,
        // Subpixel + monospace numerals.
        fontVariantNumeric: 'tabular-nums',
        whiteSpace: 'nowrap',
        WebkitFontSmoothing: 'antialiased',
        MozOsxFontSmoothing: 'grayscale',
      }}
      role="tooltip"
    >
      {children}
    </div>
  );
}

interface ValueChipProps {
  /** Series label (e.g. analyte name). Optional. */
  label?: string;
  /** Numeric value to display (will be formatted via `format`). */
  value: number | string;
  /** Optional unit suffix (default 'mg/L' per CLAUDE.md). */
  unit?: string;
  /** Custom number formatter. Default: 3 sig figs for numbers <1, else 2 decimals. */
  format?: (v: number) => string;
  /** Optional accent color for the label dot. */
  color?: string;
}

function defaultFormat(v: number): string {
  if (!isFinite(v)) return '—';
  const abs = Math.abs(v);
  if (abs === 0) return '0';
  if (abs < 0.01) return v.toExponential(2);
  if (abs < 1) return v.toPrecision(3);
  return v.toFixed(2);
}

/**
 * The actual inner chip content. Renderable inside `<ChartTooltip>` OR
 * picked up by the hand-pointer (H6) to display next to the cursor.
 */
export function ValueChip({
  label,
  value,
  unit = 'mg/L',
  format = defaultFormat,
  color,
}: ValueChipProps) {
  const formatted = typeof value === 'number' ? format(value) : value;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
      {label && (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
          {color && (
            <span
              style={{
                display: 'inline-block',
                width: 6,
                height: 6,
                borderRadius: 999,
                background: color,
              }}
            />
          )}
          <span style={{ color: 'var(--dv-tooltip-label, rgba(255, 255, 255, 0.7))', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            {label}
          </span>
        </span>
      )}
      <span style={{ fontFamily: 'var(--dv-font-mono, ui-monospace, SF Mono, monospace)', fontWeight: 600 }}>
        {formatted}
      </span>
      {unit && (
        <span style={{ color: 'var(--dv-tooltip-unit, rgba(255, 255, 255, 0.55))', fontSize: 10 }}>
          {unit}
        </span>
      )}
    </span>
  );
}
